/* eslint-disable sort-keys */
import PropTypes from 'prop-types'
import { Button, Stack, Typography, useMediaQuery } from '@mui/material'
import { all, and, dec, equals, inc, lt, not, prop } from 'ramda'
import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  setCurrentStep,
  setIsSpotlightOpen,
  setTotalSteps,
  setType
} from './spotlightSlice'
import useGetHelperList from './useGetHelperList'
import { setIsSidebarOpen } from 'components/sidebar/sidebarSlice'
import { useAnalytics } from 'hooks'

import Icon from 'components/icon/Icon'

const IntroductionContent = ({ hasHelperList = false }) => {
  const dispatch = useDispatch()
  const { currentStep, totalSteps } = useSelector(prop('spotlight'))
  const { hide_resources, letters } = useSelector(prop('school'))
  const { is_secondary_user } = useSelector(prop('user'))
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('laptop'))
  const list = useGetHelperList()
  const { sendEvent } = useAnalytics()

  const title = useMemo(() => {
    if (hide_resources) {
      if (equals(0, currentStep)) return 'Discover Content'
      if (equals(1, currentStep)) return 'Your Progress'
      if (equals(2, currentStep)) return 'All About YOU'
      if (equals(3, currentStep)) return 'Your Priorities'
      if (equals(4, currentStep)) return 'Check In'
      if (equals(5, currentStep)) return 'Personalize It'
    }
    if (equals(0, currentStep)) return 'Discover Content'
    if (equals(1, currentStep)) return 'Your Progress'
    if (equals(2, currentStep)) return 'Your Campus Connection'
    if (equals(3, currentStep)) return 'All About YOU'
    if (equals(4, currentStep)) return 'Your Priorities'
    if (equals(5, currentStep)) return 'Check In'
    if (equals(6, currentStep)) return 'Personalize It'
  }, [currentStep, hide_resources])

  const subtitle = useMemo(() => {
    if (hide_resources) {
      if (equals(0, currentStep))
        return `Navigate the huge YOU at ${letters} database to discover the topics and tools that best match your interests.`
      if (equals(1, currentStep))
        return 'Keep track of everything you find and follow through on your goals.'
      if (equals(2, currentStep))
        return `Here’s where you’ll find a snapshot of your Self Checks${is_secondary_user ? '' : ', Goals,'} and Favorites.`
      if (equals(3, currentStep))
        return 'What’s most important to YOU? Choose topics as your Priorities, and they’ll show up here as shortcuts.'
      if (equals(4, currentStep))
        return 'How are you feeling right here, right now? Jump straight to the most relevant content with one click.'
      if (equals(5, currentStep)) return `Make YOU at ${letters} your own!`
    }
    if (equals(0, currentStep))
      return `Navigate the huge YOU at ${letters} database to discover the topics and tools that best match your interests.`
    if (equals(1, currentStep))
      return 'Keep track of everything you find and follow through on your goals.'
    if (equals(2, currentStep))
      return `Use these school-specific resources to get the most out of your time at ${letters}.`
    if (equals(3, currentStep))
      return `Here’s where you’ll find a snapshot of your Self Checks${is_secondary_user ? '' : ', Goals,'} and Favorites.`
    if (equals(4, currentStep))
      return 'What’s most important to YOU? Choose topics as your Priorities, and they’ll show up here as shortcuts.'
    if (equals(5, currentStep))
      return 'How are you feeling right here, right now? Jump straight to the most relevant content with one click.'
    if (equals(6, currentStep)) return `Make YOU at ${letters} your own!`
  }, [currentStep, letters, is_secondary_user, hide_resources])

  const handleReset = () => {
    dispatch(setIsSpotlightOpen(false))
    dispatch(setType(''))
    dispatch(setCurrentStep(0))
    dispatch(setTotalSteps(null))
  }

  const handleClose = () => {
    // Basically if they leave on last step
    // then reset redux store
    if (not(equals(totalSteps, inc(currentStep)))) {
      sendEvent('Spotlight_tour', {
        Event: 'Leave',
        Step: inc(currentStep)
      })
      return handleReset()
    }
    // at end send finish event
    // then reset redux store
    sendEvent('Spotlight_tour', {
      Event: 'Finish'
    })
    return handleReset()
  }

  const handleNext = () => {
    if (and(isMobile, equals(2, currentStep))) dispatch(setIsSidebarOpen(false))
    if (all(equals(true))([isMobile, hide_resources, equals(4, currentStep)]))
      dispatch(setIsSidebarOpen(true))
    if (
      all(equals(true))([isMobile, not(hide_resources), equals(5, currentStep)])
    )
      dispatch(setIsSidebarOpen(true))
    if (equals(dec(totalSteps), currentStep)) {
      dispatch(setIsSidebarOpen(false))
      return handleClose()
    }
    return dispatch(setCurrentStep(inc(currentStep)))
  }

  return (
    <Stack
      sx={{
        maxWidth: { mobile: 'unset', laptop: 490 },
        minWidth: { mobile: 'unset', laptop: 490 },
        px: 2,
        py: 2.5,
        width: 1
      }}
    >
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography variant="h5">{title}</Typography>
          <Typography variant="body1">{subtitle}</Typography>
        </Stack>
        {hasHelperList &&
          prop(currentStep, list)?.map(
            ({ icon, isShown = true, subtitle, title }) =>
              isShown && (
                <Stack direction="row" key={title} spacing={1.5}>
                  <Icon fill>{icon}</Icon>
                  <Stack spacing={0.5}>
                    <Typography variant="overline">{title}</Typography>
                    <Typography variant="body1">{subtitle}</Typography>
                  </Stack>
                </Stack>
              )
          )}
      </Stack>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        sx={{ mt: 3 }}
      >
        <Typography sx={{ color: 'surface.onSurface' }} variant="subtitle2">
          {inc(currentStep)}/{totalSteps}
        </Typography>
        <Stack alignItems="center" direction="row" spacing={2}>
          {lt(currentStep, dec(totalSteps)) && (
            <Button onClick={handleClose} variant="outlined">
              End Tour
            </Button>
          )}
          <Button endIcon={<Icon>arrow_forward</Icon>} onClick={handleNext}>
            {equals(dec(totalSteps), currentStep) ? 'Done' : 'Got It'}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}

IntroductionContent.propTypes = {
  hasHelperList: PropTypes.bool
}

export default IntroductionContent
